// Service for interacting with the RAG Pipeline API

const RAG_API_BASE_URL = process.env.REACT_APP_RAG_API_URL || 'https://agentassist-rag-api-heahfkbnhcehbffd.eastus-01.azurewebsites.net';

const parseEventPayload = (rawEvent) => {
  const dataLines = rawEvent
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim());

  if (dataLines.length === 0) {
    return null;
  }

  return JSON.parse(dataLines.join('\n'));
};

const searchResolutionStepsLegacy = async (query, context = {}) => {
  const response = await fetch(`${RAG_API_BASE_URL}/api/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, ...context }),
  });

  if (!response.ok) {
    throw new Error(`RAG API error: ${response.status} ${response.statusText}`);
  }

  return await response.json();
};

/**
 * Search for resolution steps using the RAG pipeline
 * @param {string} query - The incident description or query
 * @returns {Promise<Object>} - Response containing resolution steps and similar tickets
 */
export const searchResolutionSteps = async (query, context = {}, streamHandlers = {}) => {
  const {
    onStatus,
    onToken,
    onComplete,
    onCached,
    signal,
  } = streamHandlers;

  try {
    const response = await fetch(`${RAG_API_BASE_URL}/api/rag-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
      body: JSON.stringify({ query, ...context }),
    });

    if (!response.ok) {
      // Backward-compatible fallback if streaming endpoint is unavailable.
      return await searchResolutionStepsLegacy(query, context);
    }

    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (!contentType.includes('text/event-stream') || !response.body) {
      return await response.json();
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffered = '';
    let accumulated = '';
    let finalPayload = null;

    while (true) {
      const { value, done } = await reader.read();
      buffered += decoder.decode(value || new Uint8Array(), { stream: !done });

      const events = buffered.split('\n\n');
      buffered = events.pop() || '';

      for (const rawEvent of events) {
        if (!rawEvent.trim()) {
          continue;
        }

        let payload;
        try {
          payload = parseEventPayload(rawEvent);
        } catch (parseError) {
          console.warn('Skipping malformed SSE event:', parseError);
          continue;
        }

        if (!payload || !payload.type) {
          continue;
        }

        if (payload.type === 'status') {
          if (typeof onStatus === 'function') {
            onStatus(payload.message || 'Working...');
          }
          continue;
        }

        if (payload.type === 'token') {
          accumulated += payload.content || '';
          if (typeof onToken === 'function') {
            onToken(accumulated, payload.content || '');
          }
          continue;
        }

        if (payload.type === 'cached') {
          finalPayload = payload.data || null;
          if (typeof onCached === 'function') {
            onCached(finalPayload);
          }
          break;
        }

        if (payload.type === 'complete') {
          finalPayload = payload.data || null;
          if (typeof onComplete === 'function') {
            onComplete(finalPayload);
          }
          break;
        }

        if (payload.type === 'error') {
          throw new Error(payload.message || 'Streaming request failed');
        }
      }

      if (finalPayload || done) {
        break;
      }
    }

    if (finalPayload) {
      return finalPayload;
    }

    if (accumulated.trim()) {
      // If the stream ended unexpectedly after sending tokens, fall back to
      // legacy endpoint so downstream consumers still receive structured data.
      return await searchResolutionStepsLegacy(query, context);
    }

    throw new Error('RAG stream ended before completion');
  } catch (error) {
    console.error('Error calling RAG API:', error);

    // Last-chance compatibility fallback.
    return await searchResolutionStepsLegacy(query, context);
  }
};

/**
 * Check RAG API health status
 * @returns {Promise<Object>} - Health status information
 */
export const checkRAGHealth = async () => {
  try {
    const response = await fetch(`${RAG_API_BASE_URL}/api/health`);
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('RAG health check failed:', error);
    throw error;
  }
};
